/**
 * Utility functions for handling trade call status and P&L bar colors
 */

/**
 * Maps status codes from API to human-readable display text
 * @param {string} status - The status code from API
 * @param {number} percentToTrigger - Percentage to trigger (if available)
 * @returns {string} 
 */
export const getStatusDisplayText = (status, percentToTrigger = null) => {
  switch (status) {
    case "LIVE_INSIDE_RANGE":
      return "LIVE (Inside Entry Price Range)";

    case "LIVE_OUTSIDE_RANGE":
      return "LIVE (Outside Entry Price range)";

    case "CLOSED_STOPLOSSHIT":
      return "CLOSED (SL hit)";

    case "CLOSED_TARGETHIT":
      return "CLOSED (Target hit)";

    case "YET_TO_TRIGGER":
      if (percentToTrigger !== null && percentToTrigger !== undefined) {
        return `Yet To Trigger / ${Math.abs(percentToTrigger).toFixed(2)}% to trigger`;
      }
      return "Yet To Trigger";

    case "EXITED":
      return "Exited";

    case "IGNORED":
    case "DISABLED":
      return "Ignored";

    default:
      if (status && status.includes("LIVE")) {
        return status.includes("INSIDE")
          ? "LIVE (Inside Entry Price Range)"
          : "LIVE (Outside Entry Price range)";
      }
      if (status && status.includes("CLOSED")) {
        return status.includes("STOPLOSS") || status.includes("SL")
          ? "CLOSED (SL hit)"
          : "CLOSED (Target hit)";
      }
      return percentToTrigger !== null && percentToTrigger !== undefined
        ? `Yet To Trigger / ${Math.abs(percentToTrigger).toFixed(2)}% to trigger`
        : "Yet To Trigger";
  }
};

/**
 * Determines the P&L bar color based on status and price position
 * @param {string} status - The status code from API
 * @param {number} currentPrice - Current market price
 * @param {number} entryLower - Lower entry price
 * @param {number} entryUpper - Upper entry price
 * @param {number} stopLoss - Stop loss price
 * @param {number} target - Target price
 * @param {string} transactionType - BUY or SELL
 * @returns {string} Hex color code for the P&L bar
 */
export const getPnLBarColor = (
  status,
  currentPrice,
  entryLower,
  entryUpper,
  stopLoss,
  target,
  transactionType = "BUY"
) => {
  const price = parseFloat(currentPrice);
  const entryLowerPrice = parseFloat(entryLower);
  const entryUpperPrice = parseFloat(entryUpper);
  const stopLossPrice = parseFloat(stopLoss);
  const targetPrice = parseFloat(target);

  const BLUE = "#1569AB";
  const GREEN = "#1E8C45";
  const RED = "#E53935";

  if (status === "CLOSED_TARGETHIT") {
    return GREEN;
  }

  if (status === "CLOSED_STOPLOSSHIT") {
    return RED;
  }

  if (status === "LIVE_INSIDE_RANGE") {
    return GREEN;
  }

  if (status === "LIVE_OUTSIDE_RANGE") {
    return RED;
  }


  if (transactionType === "BUY") {
    if (price >= targetPrice) {
      return GREEN; // At or above target
    } else if (price <= stopLossPrice) {
      return RED; // At or below stop loss
    } else if (price >= entryLowerPrice && price <= entryUpperPrice) {
      return GREEN; // Inside entry range
    } else if (price < entryLowerPrice) {
      return RED; // Below entry range
    } else {
      return BLUE; // Above entry range but below target
    }
  } else {
    // For SELL trades (inverse logic)
    if (price <= targetPrice) {
      return GREEN; // At or below target (profit for SELL)
    } else if (price >= stopLossPrice) {
      return RED; // At or above stop loss (loss for SELL)
    } else if (price >= entryLowerPrice && price <= entryUpperPrice) {
      return GREEN; // Inside entry range
    } else if (price > entryUpperPrice) {
      return RED; // Above entry range (loss for SELL)
    } else {
      return BLUE; // Below entry range but above target
    }
  }
};

/**
 * Determines the status category for filtering and display
 * @param {string} status - The status code from API
 * @returns {string} Status category: 'live', 'closed', or 'yet_to_trigger'
 */
export const getStatusCategory = (status) => {
  if (!status) return "yet_to_trigger";

  if (status.includes("LIVE")) {
    return "live";
  }

  if (status.includes("CLOSED")) {
    return "closed";
  }

  return "yet_to_trigger";
};
